import { useTranslation } from "react-i18next";
import styled, { DefaultTheme } from "styled-components";

import { Flex } from "../../../components/Flex";
import { Text } from "../../../components/Text";
import { TableActionableCellEmail } from "../../../components/table/TableActionableCellEmail";
import { TableActionableCellPhone } from "../../../components/table/TableActionableCellPhone";
import { Entry } from "../../../model/entry/Entry";
import { EntryKind } from "../../../model/entry/types/EntryKind.enum";
import { entrySliceSelectors } from "../../../redux/entry/entry.selector";
import { useAppSelector } from "../../../redux/store";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { Spacing } from "../../../theme/Spacing";
import { standardDate } from "../../../utils/Date";
import { EntryKindBadge } from "../../entries/components/EntryKindBadge";
import { renderHighlight } from "../utils/searchHighlight";

// Outside the entries table the kind colours its cells' own classes would give
// aren't there, so the cells get the same colours here
const Contacts = styled.div<{ $kind: EntryKind }>`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: ${Spacing.s};

  .actionable-cell {
    background-color: ${({ theme, $kind }) =>
      theme[$kind.toLowerCase() as keyof DefaultTheme] as string};
    color: ${({ theme, $kind }) =>
      theme[
        `on${$kind[0]}${$kind.slice(1).toLowerCase()}` as keyof DefaultTheme
      ] as string};

    p {
      color: inherit;
    }
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: ${Spacing.m};
`;

const Field = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.xs};
  min-width: 0;
`;

type Props = {
  entry: Entry & { parentForm?: { title?: string } };
};

export const SearchCardEntry: React.FC<Props> = ({ entry }) => {
  const { t } = useTranslation();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const tableColumns =
    useAppSelector(entrySliceSelectors.selectUserEntryTableSettings)?.columns ||
    [];

  const otherFields = Object.entries(entry.fields || {}).filter(
    ([key, value]) =>
      key !== "email" &&
      key !== "phone" &&
      value !== undefined &&
      value !== null &&
      String(value).length > 0,
  );

  const rows = [
    ...(entry.parentForm?.title
      ? [
          {
            key: "form",
            label: t("common.form"),
            value: entry.parentForm.title,
          },
        ]
      : []),
    ...otherFields.map(([key, value]) => ({
      key,
      label: key.startsWith("custom-field-")
        ? tableColumns.find((c) => c.field === key)?.label
        : t(`common.${key}`),
      value:
        renderHighlight(entry, key) ||
        (key === "created" || key === "updatedAt"
          ? standardDate<string>(new Date(value), user, true)
          : String(value)),
    })),
    ...(entry.description
      ? [
          {
            key: "description",
            label: t("common.description"),
            value: renderHighlight(entry, "description") || entry.description,
          },
        ]
      : []),
  ];

  return (
    <Flex flexDirection="column" gap="m" flexGrow={1}>
      <Flex alignItems="center" gap="s">
        <EntryKindBadge kind={entry.kind} />
      </Flex>

      {entry.fields?.email || entry.fields?.phone ? (
        <Contacts $kind={entry.kind}>
          <TableActionableCellEmail
            email={entry.fields?.email}
            extra={{ kind: entry.kind }}
          />
          <TableActionableCellPhone
            phone={entry.fields?.phone}
            extra={{ kind: entry.kind }}
          />
        </Contacts>
      ) : null}

      {rows.length ? (
        <FieldsGrid>
          {rows.map(({ key, label, value }) => (
            <Field key={key}>
              <Text fontSize="xs" color="darkerSub">
                {label}
              </Text>
              <Text fontSize="s" wordBreak="break-word">
                <bdi>{value}</bdi>
              </Text>
            </Field>
          ))}
        </FieldsGrid>
      ) : null}
    </Flex>
  );
};
